import { isEmpty } from 'lodash'
import { Sentence } from 'schemas/SentenceSchema'
import { SentenceHighlightInfo } from 'schemas/SentenceHighlightInfoSchema'
import { MessageConst } from 'enums'
import StreamSymbol from 'enums/StreamSymbol'
import { Metadata } from './metadata'
import { DocumentMetadataAdaptor } from './documentMetadataAdaptor'

const isNotHitSentence = (sentence: Sentence) => {
  const { text, sentences } = sentence.properties
  return sentences!.indexOf(text) === -1
}

const isInternal = (documentMetadataAdaptor: DocumentMetadataAdaptor) =>
  !isEmpty(documentMetadataAdaptor.getDataStreams()) &&
  documentMetadataAdaptor.getDataStreams()![0] === StreamSymbol.VOC

export class SentenceMetadataAdaptor implements Metadata {
  constructor(
    private documentMetadataAdaptor: DocumentMetadataAdaptor,
    private sentence: Sentence,
    private vocDisplay?: boolean,
    private needTruncated?: boolean,
    private truncatedLength?: number
  ) {}

  getTimeZone = () => this.documentMetadataAdaptor.getTimeZone()

  isVocDisplay = () => this.vocDisplay

  isNeedTruncated = () => this.needTruncated

  getTruncatedLength = () => this.truncatedLength

  getWidgetType = () => undefined

  getAuthor = () => this.documentMetadataAdaptor.getAuthor()

  getAuthorId = () => this.documentMetadataAdaptor.getAuthorId()

  getAuthorKarma = () => this.documentMetadataAdaptor.getAuthorKarma()

  getAuthorLink = () => this.documentMetadataAdaptor.getAuthorLink()

  getAuthorLocation = () => this.documentMetadataAdaptor.getAuthorLocation()

  getAuthorName = () => this.documentMetadataAdaptor.getAuthorName()

  getAuthorUrl = () => this.documentMetadataAdaptor.getAuthorUrl()

  getAuthorHandle = () => this.documentMetadataAdaptor.getAuthorHandle()

  getAvatarUrl = () => this.documentMetadataAdaptor.getAvatarUrl()

  getOriginalAuthorAvatarUrl = () => this.documentMetadataAdaptor.getOriginalAuthorAvatarUrl()

  getOriginalAuthorHandle = () => this.documentMetadataAdaptor.getOriginalAuthorHandle()

  getDateTime = () => this.documentMetadataAdaptor.getDateTime()

  getDisplayTextRange = () =>
    this.sentence.properties.displayTextRange ? [this.sentence.properties.displayTextRange] : []

  getDocumentId = () => this.documentMetadataAdaptor.getDocumentId()

  getDomain = () => this.documentMetadataAdaptor.getDomain()

  getHighlightingInfos = () => {
    if (isNotHitSentence(this.sentence) || isInternal(this.documentMetadataAdaptor)) {
      return [this.sentence.sentenceHighlightInfo]
    }

    const { text, sentences } = this.sentence.properties
    const hitSentenceIndex = sentences!.indexOf(text)

    const sentenceHighlightInfos: (SentenceHighlightInfo | undefined)[] = []
    sentences!.forEach((item, index) => {
      sentenceHighlightInfos[index] = undefined

      if (index === hitSentenceIndex) {
        sentenceHighlightInfos[index] = this.sentence.sentenceHighlightInfo
      }
    })

    return sentenceHighlightInfos
  }

  isDocumentMode = () => false

  getOriginalAuthor = () => this.documentMetadataAdaptor.getOriginalAuthor()

  getOriginalAuthorName = () => this.documentMetadataAdaptor.getOriginalAuthorName()

  getOriginalAuthorUrl = () => this.documentMetadataAdaptor.getOriginalAuthorUrl()

  getPostType = () => this.documentMetadataAdaptor.getPostType()

  getPostId = () => this.documentMetadataAdaptor.getPostId()

  getSentences = () =>
    isNotHitSentence(this.sentence) || isInternal(this.documentMetadataAdaptor)
      ? [this.sentence.properties.text!]
      : (this.sentence.properties.sentences as string[])

  getSentenceInfos = () => this.documentMetadataAdaptor.getSentenceInfos()

  getSoundbiteSentiment = () => this.sentence.sentiment

  getUrl = () => this.documentMetadataAdaptor.getUrl()

  getFavicon = () => undefined

  getMentionedUrls = () => this.documentMetadataAdaptor.getMentionedUrls()

  getAuthorFollower = () => this.documentMetadataAdaptor.getAuthorFollower()

  getAuthorGender = () => this.documentMetadataAdaptor.getAuthorGender()

  getAuthorGeoInfos = () => this.documentMetadataAdaptor.getAuthorGeoInfos()

  isTweet = () => this.documentMetadataAdaptor.isTweet()

  getDataStreams = () => this.documentMetadataAdaptor.getDataStreams()

  getAnnotatedSentiment = () => this.documentMetadataAdaptor.getAnnotatedSentiment()

  isDeleted = () => this.documentMetadataAdaptor.isDeleted()

  isSimilar = () => MessageConst.NO

  getDocumentMetaTags = () => this.sentence.documentMetaTags as string[]

  getSentenceAllTags = () =>
    isNotHitSentence(this.sentence) || isInternal(this.documentMetadataAdaptor)
      ? [[]]
      : this.documentMetadataAdaptor.getSentenceAllTags()

  getSentenceUserTags = () => [[]]

  getSentenceMetaTags = () => [[]]

  getUserTags = () => this.sentence.userTags as string[]

  getMetaTags = () => this.sentence.metaTags as string[]

  getTitle = () => this.documentMetadataAdaptor.getTitle()

  isAuthorVerified = () => this.documentMetadataAdaptor.isAuthorVerified()

  isOriginalAuthorVerified = () => this.documentMetadataAdaptor.isOriginalAuthorVerified()

  getSourceType = () => this.documentMetadataAdaptor.getSourceType()

  getSourceName = () => this.documentMetadataAdaptor.getSourceName()

  getHitSentence = () => this.sentence.properties.text

  getProfessions = () => this.documentMetadataAdaptor.getProfessions()

  getInterests = () => this.documentMetadataAdaptor.getInterests()

  getEngagement = () =>
    Number(this.getReplies()) +
    Number(this.getRetweets()) +
    Number(this.getLikes()) +
    Number(this.getViews())

  getReplies = () => this.documentMetadataAdaptor.getReplies()

  getComments = () => undefined

  getImpressions = () => undefined

  getShares = () => undefined

  getNegSentiment = () => undefined

  getPassion = () => undefined

  getPosSentiment = () => undefined

  getReblogs = () => undefined

  getSentiments = () => undefined

  getRetweets = () => this.documentMetadataAdaptor.getRetweets()

  getLikes = () => this.documentMetadataAdaptor.getLikes()

  getViews = () => this.documentMetadataAdaptor.getViews()

  getScore = () => undefined

  getProductName = () => this.documentMetadataAdaptor.getProductName()

  getDocHierarchy = () => this.documentMetadataAdaptor.getDocHierarchy()

  getPostRating = () => this.documentMetadataAdaptor.getPostRating()

  getPhotoUserTags = () => this.documentMetadataAdaptor.getPhotoUserTags()

  getAdsType = () => this.documentMetadataAdaptor.getAdsType()

  getPersistId = () => this.documentMetadataAdaptor.getPersistId()

  getOverallStarRating = () => this.documentMetadataAdaptor.getOverallStarRating()

  getOverallSurveyScore = () => this.documentMetadataAdaptor.getOverallSurveyScore()

  getNetPromoterScore = () => this.documentMetadataAdaptor.getNetPromoterScore()

  getMeasurements = () => {
    if (isNotHitSentence(this.sentence) || isInternal(this.documentMetadataAdaptor)) {
      return this.sentence.properties.measurement
        ? [this.sentence.properties.measurement]
        : undefined
    }

    return this.documentMetadataAdaptor.getMeasurements()
  }

  getSentenceMeasurements = () => this.documentMetadataAdaptor.getSentenceMeasurements()

  getParentDistilledTexts = () => this.documentMetadataAdaptor.getParentDistilledTexts()

  getParentDisplayTextRange = () => this.documentMetadataAdaptor.getParentDisplayTextRange()

  getParentMentionedUrls = () => this.documentMetadataAdaptor.getParentMentionedUrls()

  getFingerprint = () => this.sentence.fingerprint

  getSentenceNumber = () => this.sentence.properties.sentenceNumber
}

export default DocumentMetadataAdaptor
